import { clsx } from "@trydig/ui";
import type { ComponentProps, ReactNode } from "react";
import { Container } from "@/components/ui/Container";
import { Link } from "@/components/ui/Link";
import { ProductCard } from "@/components/ui/ProductCard";
import { CarouselTrack } from "@/components/ui/sections/_lib/CarouselTrack";
import { Typography } from "@/components/ui/Typography";
import type { ProductsRoute } from "@/data/routes/products.route";
import { Link as NextLink } from "@/i18n/routing";
import { isDarkMode } from "@/lib/utils/isDarkMode";

export interface ExplorationProductInContentProps extends ComponentProps<"section"> {
	heading?: string;
	linkLabel?: string;
	linkHref?: string;
	bodyText?: ReactNode;
	products: ProductsRoute["exploration"]["products"];
	bg?: string;
}

export const ExplorationProductInContent = ({
	heading,
	linkLabel,
	linkHref,
	bodyText,
	products,
	className,
	bg,
	...rest
}: ExplorationProductInContentProps) => {
	const items = products ?? [];
	const hasHeading = Boolean(heading);
	const hasLink = Boolean(linkLabel && linkHref);
	const darkMode = isDarkMode(bg);

	return (
		<section {...rest} style={{ backgroundColor: bg ? bg : undefined }}>
			<Container>
				<div
					className={clsx(
						"py-grid-section-vertical-lg flex flex-col gap-grid-section-vertical-md md:grid md:grid-cols-12 md:gap-x-10 md:gap-y-grid-section-vertical-md",
						className,
					)}
				>
					<div className="flex w-full flex-col gap-grid-section-vertical-md md:col-span-5 md:col-start-1 md:row-start-1">
						{(hasHeading || hasLink) && (
							<div className="flex w-full items-end gap-grid-component-spacing-2xs">
								<div className="flex min-w-px flex-1 flex-col gap-grid-component-spacing-xs">
									{heading && (
										<Typography
											as="h2"
											variant="heading-display-xs"
											className={clsx(
												"w-full text-text-dark-text-1",
												darkMode && "text-text-light-text-1",
											)}
										>
											{heading}
										</Typography>
									)}
								</div>
								{hasLink && linkHref && (
									<Link
										as={NextLink}
										href={linkHref}
										variant="primary"
										size="medium"
										className={clsx(darkMode && "text-text-light-text-1")}
									>
										{linkLabel}
									</Link>
								)}
							</div>
						)}
						{bodyText && (
							<div
								className={clsx(
									"flex flex-col gap-grid-component-spacing-md text-text-dark-text-1 font-body text-sm leading-[1.5] [&_a]:underline [&_ol]:list-decimal [&_ol]:pl-4 [&_ul]:list-disc [&_ul]:pl-4",
									darkMode && "text-text-light-text-1",
								)}
							>
								{bodyText}
							</div>
						)}
					</div>
					<CarouselTrack
						layout="pair"
						desktopAlwaysCarousel
						className="md:col-span-6 md:col-start-7 md:row-start-1"
						darkMode={darkMode}
					>
						{items.map((product) => (
							<ProductCard key={product.id} product={product} size="large" darkMode={darkMode} />
						))}
					</CarouselTrack>
				</div>
			</Container>
		</section>
	);
};
